
import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { 
  ArrowRight, 
  Info, 
  Home, 
  Wheat, 
  Heart, 
  GraduationCap,
  Users,
  Droplets,
  Zap,
  Building,
  Recycle,
  Sparkles,
  CloudSun,
  Fish,
  Leaf,
  Scale,
  Network,
  Handshake
} from "lucide-react";
import { sdgsData } from "@/data/sdgs";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";

// Map SDG IDs to appropriate icons
const sdgIcons = {
  1: Home,
  2: Wheat,
  3: Heart,
  4: GraduationCap,
  5: Users,
  6: Droplets,
  7: Zap,
  8: Building,
  9: Network,
  10: Scale,
  11: Building,
  12: Recycle,
  13: CloudSun,
  14: Fish,
  15: Leaf,
  16: Scale,
  17: Handshake
};

const SDGsPage: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Header */}
      <header className="pt-32 pb-12 bg-palsustain-green/90">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            {t("home.sdgs.title")}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
            {t("home.sdgs.subtitle")}
          </p>
        </div>
      </header>
      
      {/* SDGs Grid */}
      <section className="py-12 bg-palsustain-cream">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {sdgsData.map((sdg) => {
              const IconComponent = sdgIcons[sdg.id as keyof typeof sdgIcons] || Sparkles;
              
              return (
                <div
                  key={sdg.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 flex flex-col h-full"
                  style={{ borderTop: `4px solid ${sdg.color}` }}
                >
                  <div className="p-6 flex flex-col h-full">
                    <div className="flex items-start mb-4">
                      <div 
                        className="w-14 h-14 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
                        style={{ backgroundColor: `${sdg.color}15` }}
                      >
                        <IconComponent 
                          className="w-8 h-8"
                          style={{ color: sdg.color }}
                        />
                      </div>
                      <div>
                        <span 
                          className="text-sm font-medium px-3 py-1 rounded-full inline-block mb-2" 
                          style={{ backgroundColor: sdg.color, color: 'white' }}
                        >
                          {language === "en" ? `Goal ${sdg.id}` : `الهدف ${sdg.id}`}
                        </span>
                        <h3 className="text-xl font-bold text-palsustain-charcoal">
                          {language === "en" ? sdg.title.en : sdg.title.ar}
                        </h3>
                      </div>
                    </div>
                    
                    <p className="text-palsustain-charcoal/80 mb-4 flex-grow">
                      {language === "en" ? sdg.description.en : sdg.description.ar}
                    </p>
                    
                    <div className="mt-auto">
                      <Link to={`/sdgs/${sdg.slug}`} className="w-full">
                        <button 
                          className="w-full text-left py-2 px-3 text-sm text-palsustain-green border border-palsustain-green/30 rounded-md hover:bg-palsustain-green/5 transition-colors"
                        >
                          {language === "en" 
                            ? "Discover how this goal is applied in Palestinian context" 
                            : "اكتشف كيف يتم تطبيق هذا الهدف في السياق الفلسطيني"}
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default SDGsPage;
